import React from 'react';
import { X, Trash2, AlertTriangle, Loader2 } from 'lucide-react';

interface DeleteConfirmDialogProps {
 /** Title of the item being deleted, shown in the body for context. */
 itemTitle: string;
 /** Whether a delete request is currently in flight. */
 deleting?: boolean;
 /** Optional error message to surface if a previous attempt failed. */
 error?: string | null;
 onConfirm: () => void;
 onCancel: () => void;
}

/**
 * Explicit confirm/cancel modal shown before a destructive delete.
 * Styling mirrors PracticeSummaryModal (rounded-2xl card, overlay, header/body/footer).
 */
const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
 itemTitle,
 deleting = false,
 error,
 onConfirm,
 onCancel,
}) => {
 return (
  <div
   className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
   onClick={deleting ? undefined : onCancel}
   role="dialog"
   aria-modal="true"
   aria-labelledby="delete-confirm-title"
  >
   <div
    className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden"
    onClick={(e) => e.stopPropagation()}
   >
    {/* Header */}
    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
     <div className="flex items-center gap-3 min-w-0">
      <span className="w-9 h-9 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 flex items-center justify-center flex-shrink-0">
       <AlertTriangle className="w-5 h-5" />
      </span>
      <h3 id="delete-confirm-title" className="text-base font-bold text-gray-900 dark:text-white">
       Delete this problem?
      </h3>
     </div>
     <button
      onClick={onCancel}
      disabled={deleting}
      className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors flex-shrink-0 disabled:opacity-50"
      title="Close"
     >
      <X className="w-5 h-5" />
     </button>
    </div>

    {/* Body */}
    <div className="px-6 py-5">
     <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
      You’re about to permanently delete{' '}
      <span className="font-semibold text-gray-900 dark:text-white">{itemTitle || 'this problem'}</span>.
      This also removes any saved progress for it. This action can’t be undone.
     </p>
     {error && (
      <p className="mt-3 text-sm text-rose-600 dark:text-rose-400">{error}</p>
     )}
    </div>

    {/* Footer */}
    <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-800 flex-shrink-0 bg-gray-50 dark:bg-gray-800/40">
     <button
      onClick={onCancel}
      disabled={deleting}
      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
     >
      Cancel
     </button>
     <button
      onClick={onConfirm}
      disabled={deleting}
      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-rose-600 hover:bg-rose-700 text-white transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
     >
      {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
      Delete
     </button>
    </div>
   </div>
  </div>
 );
};

export default DeleteConfirmDialog;
